import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Folder, Trash2, XCircle } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { APPT_TYPES, ymd } from "@/components/appointment-item";
import { Header } from "@/components/header";
import { Button, Card, ErrorView, Field, Loading } from "@/components/kit";
import { Sheet } from "@/components/sheet";
import { useToast } from "@/components/toast";

const DURATIONS = [30, 60, 90, 120];
const toFr = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const fromFr = (s) => { const m = s.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); return m ? `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}` : null; };
const normTime = (s) => { const m = s.trim().match(/^(\d{1,2})[:hH](\d{2})?$/); if (!m) return null; const h = +m[1], mi = +(m[2] ?? 0); return h < 24 && mi < 60 ? `${String(h).padStart(2, "0")}:${String(mi).padStart(2, "0")}` : null; };
const addMin = (t, n) => { const [h, m] = t.split(":").map(Number); const x = Math.min(h * 60 + m + n, 23 * 60 + 59); return `${String(Math.floor(x / 60)).padStart(2, "0")}:${String(x % 60).padStart(2, "0")}`; };
const durLabel = (m) => (m < 60 ? `${m} min` : `${m / 60} h`.replace(".5", " h 30").replace(" h h", " h"));

function Chip({ label, on, onClick, testID, disabled }) {
  return (
    <button type="button" data-testid={testID} aria-pressed={on} disabled={disabled} onClick={onClick}
      className={`focus-ring h-9 rounded-full border px-3 text-sm font-medium transition-colors disabled:cursor-not-allowed ${on ? "border-[#D4AF37] bg-[#292415] text-[#D4AF37]" : "border-[#333333] bg-[#2A2A2A] text-[#CCCCCC] enabled:hover:border-[#555555] disabled:opacity-50"}`}>
      {label}
    </button>
  );
}

export default function AppointmentForm() {
  const [p] = useSearchParams();
  const id = p.get("id");
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const { user } = useAuth();
  const existing = useQuery({ queryKey: ["admin-appointment", id], queryFn: () => api(`/admin/appointments/${id}`), enabled: !!id });
  const staff = useQuery({ queryKey: ["admin-staff"], queryFn: () => api("/admin/staff") });

  const [title, setTitle] = useState("");
  const [type, setType] = useState("rdv_client");
  const [date, setDate] = useState(toFr(p.get("date") || ymd(new Date())));
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("10:00");
  const [endDate, setEndDate] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [people, setPeople] = useState(user ? [user.id] : []);
  const [dossier, setDossier] = useState(p.get("dossier_id") ? { id: p.get("dossier_id"), label: p.get("dossier_label") || "Dossier lié" } : null);
  const [busy, setBusy] = useState(false);
  const [delOpen, setDelOpen] = useState(false);

  useEffect(() => {
    const a = existing.data;
    if (!a) return;
    setTitle(a.title); setType(a.type); setDate(toFr(a.start)); setStart(a.start.slice(11, 16)); setEnd(a.end.slice(11, 16));
    setEndDate(a.end.slice(0, 10) !== a.start.slice(0, 10) ? toFr(a.end) : "");
    setLocation(a.location ?? ""); setNotes(a.notes ?? ""); setPeople(a.participant_ids);
    setDossier(a.dossier ? { id: a.dossier.id, label: `${a.dossier.number} · ${a.dossier.client_name}` } : null);
  }, [existing.data]);

  const d = fromFr(date), s = normTime(start), e = normTime(end);
  const ed = endDate.trim() ? fromFr(endDate) : d;
  const dateErr = date && !d ? "Format JJ/MM/AAAA" : undefined;
  const endDateErr = endDate.trim() && !ed ? "Format JJ/MM/AAAA" : undefined;
  const timeErr = s && e && d && ed && `${ed}T${e}` <= `${d}T${s}` ? "La fin doit être après le début" : undefined;
  const readOnly = !!id && !!existing.data && !existing.data.can_edit;
  const valid = title.trim() && d && ed && s && e && !timeErr && people.length > 0 && !readOnly;

  const invalidate = () => ["admin-appointments", "admin-appointment", "admin-dossier"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const save = async (ev) => {
    ev?.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      await api(id ? `/admin/appointments/${id}` : "/admin/appointments", {
        method: id ? "PUT" : "POST",
        body: { title, type, start: `${d}T${s}`, end: `${ed}T${e}`, location: location || null, notes: notes || null, participant_ids: people, dossier_id: dossier?.id ?? null },
      });
      invalidate();
      toast(id ? "Rendez-vous modifié." : "Rendez-vous ajouté au planning.");
      navigate(-1);
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api(`/admin/appointments/${id}`, { method: "DELETE" });
      invalidate();
      setDelOpen(false);
      toast("Rendez-vous supprimé.");
      navigate(-1);
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="appointment-form-screen" className="flex flex-col">
      <Header title={id ? "Rendez-vous" : "Nouveau rendez-vous"} />
      {id && existing.isLoading ? <Loading /> : id && existing.isError ? <ErrorView message="Rendez-vous introuvable." onRetry={() => existing.refetch()} /> : (
        <form onSubmit={save} noValidate className="mx-auto flex w-full max-w-5xl flex-col gap-4 p-4 pb-8 md:p-8">
          {readOnly ? <p data-testid="appointment-readonly" className="text-sm text-[#F2994A]">Rendez-vous créé par {existing.data?.created_by.name} : seuls ses participants ou l'administrateur peuvent le modifier.</p> : null}
          <fieldset data-testid="appointment-fields" disabled={readOnly} className={`grid items-start gap-4 lg:grid-cols-2 ${readOnly ? "opacity-60" : ""}`}>
            <div className="flex flex-col gap-4">
              <Card className="flex flex-col gap-3">
                <Field testID="appointment-title-input" label="Objet *" value={title} onChangeText={setTitle} placeholder="RDV signature M. Dupont" />
                <div className="flex flex-wrap gap-2">
                  {APPT_TYPES.map((t) => <Chip key={t.key} testID={`appointment-type-${t.key}`} label={t.label} on={type === t.key} onClick={() => setType(t.key)} />)}
                </div>
              </Card>
              <Card className="flex flex-col gap-3">
                <div className="grid gap-3 md:grid-cols-2">
                  <Field testID="appointment-date-input" label="Date (JJ/MM/AAAA) *" value={date} onChangeText={setDate} error={dateErr} />
                  <Field testID="appointment-end-date-input" label="Fin le (si plusieurs jours, facultatif)" value={endDate} onChangeText={setEndDate} placeholder={date} error={endDateErr} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field testID="appointment-start-input" label="Début *" value={start} onChangeText={setStart} placeholder="09:00" error={start && !s ? "HH:MM" : undefined} />
                  <Field testID="appointment-end-input" label="Fin *" value={end} onChangeText={setEnd} placeholder="10:00" error={end && !e ? "HH:MM" : timeErr} />
                </div>
                <div className="flex flex-wrap gap-2">
                  {DURATIONS.map((m) => <Chip key={m} testID={`appointment-duration-${m}`} label={durLabel(m)} on={!!s && !!e && addMin(s, m) === e} onClick={() => s && setEnd(addMin(s, m))} disabled={readOnly} />)}
                </div>
              </Card>
              <Card className="flex flex-col gap-3">
                <Field testID="appointment-location-input" label="Lieu" value={location} onChangeText={setLocation} placeholder="Adresse, agence, visio…" />
                {dossier ? (
                  <div className="flex items-center gap-2">
                    <Folder className="h-5 w-5 text-[#D4AF37]" aria-hidden />
                    <span data-testid="appointment-dossier-label" className="flex-1 text-sm text-white">Dossier {dossier.label}</span>
                    <button type="button" aria-label="Retirer le dossier" data-testid="appointment-dossier-remove" onClick={() => setDossier(null)} className="focus-ring rounded-full text-[#9E9E9E] hover:text-white"><XCircle className="h-5 w-5" /></button>
                  </div>
                ) : null}
              </Card>
            </div>
            <div className="flex flex-col gap-4">
              <Card className="flex flex-col gap-3">
                <h2 className="t-h3">Participants *</h2>
                <div className="flex flex-wrap gap-2">
                  {(staff.data ?? []).map((u) => (
                    <Chip key={u.id} testID={`appointment-participant-${u.id}`} label={u.id === user?.id ? `${u.name} (moi)` : u.name} on={people.includes(u.id)}
                      onClick={() => setPeople(people.includes(u.id) ? people.filter((x) => x !== u.id) : [...people, u.id])} />
                  ))}
                </div>
                <p className="t-caption">Planning partagé entre le gérant et ses collaborateurs. Les agents n'y ont pas accès.</p>
              </Card>
              <Card><Field testID="appointment-notes-input" label="Notes" multiline value={notes} onChangeText={setNotes} /></Card>
            </div>
          </fieldset>
          <div className="flex flex-col gap-3 lg:ml-auto lg:w-[calc(50%-8px)]">
            {!readOnly ? <Button testID="appointment-save-button" type="submit" title={id ? "Enregistrer" : "Ajouter au planning"} size="lg" loading={busy} disabled={!valid} /> : null}
            {id && !readOnly ? <Button testID="appointment-delete-button" title="Supprimer" icon={Trash2} variant="danger" onClick={() => setDelOpen(true)} /> : null}
          </div>
        </form>
      )}
      <Sheet visible={delOpen} onClose={() => setDelOpen(false)} title="Supprimer ce rendez-vous ?" testID="appointment-delete-sheet">
        <Button testID="appointment-delete-confirm" title="Supprimer" variant="danger" loading={busy} onClick={remove} className="w-full" />
      </Sheet>
    </div>
  );
}
